import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/hero";
import type { HeroSlide } from "@/types/content";

const slides = heroSlides as HeroSlide[];

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  if (slides.length === 0) {
    return (
      <section className="w-full h-[420px] md:h-[600px] bg-background-200 flex items-center justify-center">
        <p className="text-foreground-500">暂无焦点内容</p>
      </section>
    );
  }

  const go = (dir: number) => {
    setIndex((current) => (current + dir + slides.length) % slides.length);
  };

  return (
    <section className="relative w-full h-[560px] md:h-[680px] overflow-hidden bg-foreground-950">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} 影视工厂焦点推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex items-center">
        <div className="mx-auto max-w-page w-full px-4 md:px-6 pt-16 md:pt-20">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-foreground-950 text-xs font-medium">
              <i className="ri-fire-line" />
              本周焦点推荐
            </span>
            <h1
              key={slides[index].id}
              className="mt-5 font-heading text-4xl md:text-6xl lg:text-7xl text-background-50 tracking-wide animate-fade-up"
            >
              {slides[index].title}
            </h1>
            <p className="mt-5 text-base md:text-xl text-background-200 leading-8 animate-fade-up">
              {slides[index].tagline}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${slides[index].targetId}`}
                className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm md:text-base hover:bg-primary-600 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg" />
                查看详情
              </Link>
              <Link
                to="/movie"
                className="px-6 py-3 rounded-md border border-background-50/40 text-background-50 text-sm md:text-base hover:bg-background-50/10 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-compass-3-line text-lg" />
                浏览影视库
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(-1)}
        className="hidden md:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-background-50/15 text-background-50 items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(1)}
        className="hidden md:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-background-50/15 text-background-50 items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到 ${slide.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}